import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

export const CookiePolicy = () => {
  return (
    <div className="pt-16" data-testid="cookie-policy-page">
      <SEO 
        title="Cookie Policy"
        description="Learn about how Turcoaz Apps uses cookies and similar technologies on our website. Understand your consent choices and how to manage cookie preferences."
        canonical="/cookie-policy"
      />
      {/* Header */}
      <section className="py-20 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Legal
            </p>
            <h1 
              className="text-4xl sm:text-5xl tracking-tight leading-none font-semibold text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Cookie Policy
            </h1>
            <p className="text-sm text-slate-500 mb-4">
              Last updated: January 2025
            </p>
            <p className="text-slate-600 leading-relaxed">
              This Cookie Policy explains how Turcoaz Apps uses cookies and similar tracking technologies when you visit turcoazapps.com and our related services, including Smart PDF Tools at pdf.turcoazapps.com. It describes what these technologies are, why we use them, and your rights to control their use.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">

            {/* 1. What Are Cookies */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              1. What Are Cookies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used by website owners to make their websites work, to work more efficiently, and to provide reporting information. Cookies set by the website owner (in this case, Turcoaz Apps) are called "first-party cookies." Cookies set by parties other than the website owner are called "third-party cookies."
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              In addition to cookies, we may use similar technologies such as pixel tags, web beacons, and local storage to collect information about how you interact with our Services. For the purposes of this policy, we refer to all of these technologies collectively as "cookies."
            </p>

            {/* 2. Why We Use Cookies */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              2. Why We Use Cookies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We use cookies for several reasons. Some cookies are required for technical reasons in order for our website to operate, and we refer to these as "essential" cookies. Other cookies enable us to track and target the interests of our users to enhance the experience on our Services. Third parties may serve cookies through our website for advertising, analytics, and other purposes.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              The specific types of first-party and third-party cookies served through our website and the purposes they perform are described in detail below.
            </p>

            {/* 3. Essential Cookies */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              3. Essential Cookies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              These cookies are strictly necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions you take that amount to a request for services, such as setting your privacy preferences, logging in, or filling in forms.
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">
              Essential cookies we use include:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Cookie consent preference:</strong> Stores your cookie consent choice so we don't ask you every time you visit. This cookie is set by Turcoaz Apps and does not track any personal data.</li>
              <li><strong>Session cookies:</strong> Temporary cookies that are deleted when you close your browser. They help maintain your session while you navigate the website.</li>
              <li><strong>Security cookies:</strong> Used to authenticate users and prevent fraudulent use of login credentials, protecting you during your visit.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              You can set your browser to block or alert you about these cookies, but some parts of the site will not work. These cookies do not store any personally identifiable information.
            </p>

            {/* 4. Analytics Cookies */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              4. Analytics Cookies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us understand which pages are the most and least popular and see how visitors move around the site. All information these cookies collect is aggregated and therefore anonymous.
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">
              Analytics cookies we may use include:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Google Analytics:</strong> We may use Google Analytics to understand website traffic and page usage patterns. Google Analytics collects information such as how often users visit the site, what pages they visit, and what other sites they used prior to coming to our site. We use the information we get from Google Analytics to improve our Services.</li>
              <li><strong>Performance monitoring:</strong> Cookies that measure page load times and site performance to help us identify and fix issues that affect your experience.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              If you do not allow these cookies, we will not be able to include your visit in our statistics, which limits our ability to improve the site experience.
            </p>

            {/* 5. Advertising Cookies */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              5. Advertising Cookies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our website may display advertisements served by third-party advertising partners, including Google AdSense. These partners may set cookies on your device to build a profile of your interests and show you relevant ads on other sites. They do not directly store personal information but are based on uniquely identifying your browser and internet device.
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">
              Advertising cookies may be used for the following purposes:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Personalized advertising:</strong> To show ads that are relevant to your interests based on your browsing activity across websites.</li>
              <li><strong>Ad frequency capping:</strong> To limit the number of times you see a particular advertisement.</li>
              <li><strong>Ad performance measurement:</strong> To measure the effectiveness of advertising campaigns.</li>
              <li><strong>Fraud prevention:</strong> To detect and prevent click fraud and other invalid ad activity.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you do not allow advertising cookies, you will still see ads on our site, but they will be generic and not tailored to your interests.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">Google Ads Settings</a> or the <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">Network Advertising Initiative opt-out page</a>.
            </p>

            {/* 6. Functional Cookies */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              6. Functional Cookies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-6">
              <li><strong>Font loading:</strong> Cookies used by Google Fonts to deliver and optimize web fonts for a consistent visual experience.</li>
              <li><strong>Theme preferences:</strong> If applicable, cookies that remember your display preferences (such as font size or layout choices).</li>
              <li><strong>Language preferences:</strong> Cookies that remember your preferred language or region settings.</li>
            </ul>

            {/* 7. Your Consent Choices */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              7. Your Consent Choices
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              When you first visit our website, you will see a cookie consent banner at the bottom of the page. This banner gives you the choice to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Accept All:</strong> You consent to all cookies, including analytics and advertising cookies. This gives you the best experience and helps us improve our services.</li>
              <li><strong>Decline:</strong> You decline non-essential cookies. Only strictly necessary cookies will be used. The website will still function, but we won't be able to personalize your experience or measure site performance.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              Your consent choice is stored locally on your device using a first-party cookie. We do not transmit your consent preference to any external service. You can change your choice at any time by clearing your browser cookies and revisiting the site, which will cause the consent banner to appear again.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              Under the General Data Protection Regulation (GDPR) and similar privacy laws, you have the right to withdraw your consent at any time. Withdrawing consent does not affect the lawfulness of processing based on consent before its withdrawal.
            </p>

            {/* 8. How to Manage Cookies */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              8. How to Manage Cookies in Your Browser
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              In addition to the consent choices we provide, most web browsers allow you to control cookies through their settings. Here's how to manage cookies in the most popular browsers:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Google Chrome:</strong> Settings &gt; Privacy and Security &gt; Cookies and other site data</li>
              <li><strong>Mozilla Firefox:</strong> Settings &gt; Privacy &amp; Security &gt; Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Manage Website Data</li>
              <li><strong>Microsoft Edge:</strong> Settings &gt; Cookies and site permissions &gt; Manage and delete cookies and site data</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              You can also use private or incognito browsing mode to prevent cookies from being stored after your session ends. Please be aware that blocking or deleting cookies may impact your experience on our website. Some features may not function as intended without essential cookies enabled.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              For more information about cookies and how to manage them, you can visit <a href="https://www.aboutcookies.org/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">www.aboutcookies.org</a> or <a href="https://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">www.allaboutcookies.org</a>.
            </p>

            {/* 9. Third-Party Cookies */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              9. Third-Party Cookies
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Some cookies on our site are placed by third-party services that appear on our pages. We do not control the setting of these cookies. The third parties that may set cookies on our website include:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><strong>Google (Analytics and AdSense):</strong> For website analytics and advertising services. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">Google's Privacy Policy</a>.</li>
              <li><strong>Google Fonts:</strong> For delivering web fonts. Google may collect limited data about font requests.</li>
              <li><strong>Cloudflare:</strong> For content delivery, performance optimization, and security. See <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">Cloudflare's Privacy Policy</a>.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              We encourage you to review the privacy policies of these third-party services to understand how they collect and use data. We select partners that maintain strong privacy practices and comply with applicable data protection regulations.
            </p>

            {/* 10. Do Not Track */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              10. Do Not Track Signals
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Some browsers include a "Do Not Track" (DNT) feature that signals to websites that you do not want to be tracked. Because there is no widely accepted standard for how DNT signals should be interpreted, our website does not currently respond to browser DNT signals. However, you can manage your cookie preferences using the methods described above.
            </p>

            {/* 11. Changes */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              11. Changes to This Cookie Policy
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              We may update this Cookie Policy from time to time to reflect changes in our practices, the cookies we use, or for legal, operational, or regulatory reasons. When we make changes, we will update the "Last updated" date at the top of this policy and post the revised version on this page. We encourage you to review this policy periodically to stay informed about our use of cookies.
            </p>

            {/* 12. More Information */}
            <h2 
              className="text-2xl font-medium text-slate-900 mb-4 mt-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              12. More Information
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              For more details about how we handle your personal data, please read our <Link to="/privacy-policy" className="text-teal-600 hover:text-teal-700">Privacy Policy</Link>. If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <p className="text-slate-600 mb-2">
                <strong>Email:</strong>{' '}
                <a href="mailto:contact@turcoazapps.com" className="text-teal-600 hover:text-teal-700">
                  contact@turcoazapps.com
                </a>
              </p>
              <p className="text-slate-600 mb-2">
                <strong>Contact Form:</strong>{' '}
                <Link to="/contact" className="text-teal-600 hover:text-teal-700">
                  turcoazapps.com/contact
                </Link>
              </p>
              <p className="text-slate-600">
                <strong>Website:</strong>{' '}
                <a href="https://turcoazapps.com" className="text-teal-600 hover:text-teal-700">
                  turcoazapps.com
                </a>
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
